<template>
    <Modal ref="modal" :title="$t('labels.connectExternalProducts')" size="full">
        <div class="card-body" v-if="!externalProducts.length">
            <Alert type="warning">
                <span class="font-weight-semibold mr-2">{{ translate(`alerts.info`) }}</span>
                {{ $t("alerts.noExternalProductsToConnect") }}
            </Alert>
        </div>
        <NavTab class="nav-tabs-bottom nav-justified mb-0" v-else>
            <NavTabItem href="#productsIntegrationConnectProductsModalTab" ref="products">{{ $t("labels.products") }}</NavTabItem>
            <NavTabItem href="#balanceIntegrationConnectProductsModalTab">{{ $t("labels.balance") }}</NavTabItem>
            <template #content>
                <NavTabPane id="productsIntegrationConnectProductsModalTab">
                    <div class="table-responsive">
                        <table class="table table-fixed">
                            <thead>
                                <tr>
                                    <th colspan="4" width="45%">{{ $t('table.titles.externalProduct') }}</th>
                                    <th colspan="4" width="45%">{{ $t('table.titles.internalProduct') }}</th>
                                    <th width="10%"></th>
                                </tr>
                                <tr>
                                    <th width="100px">{{ $t('table.titles.id') }}</th>
                                    <th width="80px">{{ $t('table.titles.image') }}</th>
                                    <th width="200px">{{ $t('table.titles.category') }}</th>
                                    <th width="550px">{{ $t('table.titles.name') }}</th>
                                    <th width="100px">{{ $t('table.titles.id') }}</th>
                                    <th width="80px">{{ $t('table.titles.image') }}</th>
                                    <th width="200px">{{ $t('table.titles.category') }}</th>
                                    <th width="550px">{{ $t('table.titles.name') }}</th>
                                    <th width="80px"></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(externalProduct, index) in externalProducts">
                                    <td>
                                        <span>{{ externalProduct.source_id }}</span>
                                        <br><span v-if="externalProduct.second_source_id" class="text-muted">{{ externalProduct.second_source_id }}</span>
                                    </td>
                                    <td>
                                        <img class="table-image" :src="externalProduct.picture_url ?? PLACEHOLDER_IMAGE_URL" :alt="externalProduct.name"/>
                                    </td>
                                    <td>
                                        <span v-if="externalProduct.category">{{ externalProduct.category.name }}</span>
                                        <u v-else class="text-muted">{{ $t('placeholders.missing') }}</u>
                                        <br><span v-if="externalProduct.manufacturer_name" class="text-muted">{{ externalProduct.manufacturer_name }}</span>
                                        <u v-else class="text-muted">{{ $t('placeholders.missing') }}</u>
                                    </td>
                                    <td class="name-cell">
                                        <span :title="externalProduct.name">{{ externalProduct.name }}</span><br>
                                        <span v-if="externalProduct.sku" class="text-muted">{{ externalProduct.sku }}</span>
                                        <u v-else class="text-muted">{{ $t('placeholders.missing') }}</u>
                                    </td>
                                    <slot v-if="externalProduct.internalProduct">
                                        <td>
                                            <span>{{ externalProduct.internalProduct.id }}</span>
                                        </td>
                                        <td>
                                            <img class="table-image" :src="externalProduct.internalProduct.main_image_url !== null ? externalProduct.internalProduct.main_image_url : PLACEHOLDER_IMAGE_URL" :alt="externalProduct.internalProduct.name"/>
                                        </td>
                                        <td>
                                            <span v-if="externalProduct.internalProduct.category">{{ externalProduct.internalProduct.category.name }}</span>
                                            <u v-else class="text-muted">{{ $t('placeholders.missing') }}</u>
                                            <br><span v-if="externalProduct.internalProduct.manufacturer" class="text-muted">{{ externalProduct.internalProduct.manufacturer }}</span>
                                            <u v-else class="text-muted">{{ $t('placeholders.missing') }}</u>
                                        </td>
                                        <td class="name-cell">
                                            <span :title="externalProduct.internalProduct.name">{{ externalProduct.internalProduct.name }}</span><br>
                                            <span v-if="externalProduct.internalProduct.sku" class="text-muted">{{ externalProduct.internalProduct.sku }}</span>
                                            <u v-else class="text-muted">{{ $t('placeholders.missing') }}</u>
                                        </td>
                                    </slot>
                                    <td v-else colspan="4">
                                        <Autocomplete
                                            url="/catalog/product-items"
                                            :model-value="externalProduct.internalProduct?.uuid"
                                            :selected-value="externalProduct.internalProduct?.name"
                                            :queryParams="{extra: 1}"
                                            @update:model-value="selectInternalProduct(index, $event)"
                                        />
                                    </td>
                                    <td>
                                        <Button size="sm" color="light" @click="selectInternalProduct(index, null)">
                                            <span>{{ $t('buttons.replace') }}</span>
                                        </Button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </NavTabPane>
                <NavTabPane id="balanceIntegrationConnectProductsModalTab">
                    <div class="modal-body">
                        <FormGroup :label="$t('labels.needCreateArrivalDeclaration')">
                            <CheckBox v-model="needCreateArrivalDeclaration"/>
                        </FormGroup>
                        <FormGroup :label="$t('labels.warehouse')">
                            <Select
                                @update:modelValue="newWarehouse => warehouse = newWarehouse"
                                @update:search="search = $event"
                                :model-value="warehouse.id"
                                :selected-value="warehouse.name"
                                :options="warehousesOptions"
                                :has-search="true"
                                optionKey="id"
                                :disabled="!needCreateArrivalDeclaration"
                            />
                        </FormGroup>
                        <FormGroup :label="$t('labels.supplier')">
                            <Select
                                @update:modelValue="newSupplier => supplier = newSupplier"
                                :model-value="supplier.id"
                                :selected-value="supplier.name"
                                :options="suppliers"
                                optionKey="id"
                                :disabled="!needCreateArrivalDeclaration"
                            />
                        </FormGroup>
                    </div>
                </NavTabPane>
            </template>
        </NavTab>
        <div class="modal-footer" v-if="externalProducts.length">
            <Button size="sm" color="indigo" @click="connectProducts" :disabled="connectionStarted">
                <i :class="['icon-spinner4 mr-2', connectionStarted ? 'spinner' : '']"></i>
                <span>{{ $t('buttons.connect') }}</span>
            </Button>
        </div>
    </Modal>
</template>
<script>
import Modal from "../../../../components/new/modal/AppModal.vue";
import Autocomplete from "../../../../components/new/form/autocomplete/AppAutoComplete.vue";
import Button from "../../../../components/new/button/AppButton.vue";
import NavTab from "../../../components/tabs/NavTab.vue";
import NavTabItem from "../../../components/tabs/NavTabItem.vue";
import NavTabPane from "../../../components/tabs/NavTabPane.vue";
import FormGroup from "../../../../components/new/form/group/FormGroup.vue";
import CheckBox from "../../../../components/new/form/checkbox/AppCheckbox.vue";
import Select from "../../../../components/new/form/select/AppSelect.vue";
import Alert from "../../../../components/AlertComponent.vue";

import { chunk } from "../../../../utils";
import { httpClient } from "../../../../services/HttpClient";
import { PLACEHOLDER_IMAGE_URL } from "../../../constants";
import MessageNotificator from "../../../../services/MessageNotificator";

export default {
    name: "ConnectExternalProductModal",
    components: {
        Modal, Autocomplete, Button, NavTab, NavTabItem, NavTabPane, FormGroup, CheckBox,
        Select, Alert
    },
    emits: ['update:table'],
    props: ['integration'],
    data() {
        return {
            PLACEHOLDER_IMAGE_URL,
            externalProducts: [],
            needCreateArrivalDeclaration: false,
            warehouse: {
                id: null,
                name: ''
            },
            supplier: {
                id: null,
                name: ''
            },
            currency: {
                uuid: null,
                name: '',
                rate: 0
            },
            warehouses: [],
            suppliers: [],
            currencies: [],
            search: '',
            connectionStarted: false
        }
    },
    methods: {
        async show(externalProducts) {
            this.externalProducts = externalProducts.filter(externalProduct => externalProduct.product === null);
            if (this.externalProducts.length === 0) {
                this.$refs.modal.show();
                return;
            }
            this.needCreateArrivalDeclaration = false;

            let complianceArticlesWithIndices = {};
            let promises = [];

            this.externalProducts.forEach((externalProduct, index) => {
                if (externalProduct.sku !== null && externalProduct.sku !== "") {
                    complianceArticlesWithIndices[this.cleanSku(externalProduct.sku)] = index
                }
            })

            let chunkedExternalProduct = chunk(Object.keys(complianceArticlesWithIndices), 10)

            let responseData = [];

            await Promise.all(chunkedExternalProduct.map((chunk) => {
                return httpClient.get("/catalog/product-items", {
                    cleared_skus: chunk,
                    extra: 1
                })
            })).then((response) => {
                response.map(item => responseData.push(item.data.data))
            })

            if (this.warehouses.length === 0) {
                promises.push(this.fetchWarehouses());
            }
            if (this.suppliers.length === 0) {
                promises.push(this.fetchSuppliers());
            }
            if (this.currencies.length === 0) {
                promises.push(this.fetchCurrencies());
            }

            responseData.flat().forEach((finedProduct) => {
                this.selectInternalProduct(complianceArticlesWithIndices[this.cleanSku(finedProduct.sku)], finedProduct)
            })

            await Promise.all(promises);

            if (this.warehouses.length && !this.warehouse.id) {
                this.warehouse = this.warehouses[0];
            }
            if (this.suppliers.length && !this.supplier.id) {
                this.supplier = this.suppliers[0];
            }
            if (this.currencies.length && !this.currency.uuid) {
                this.currency = this.mainCurrency;
            }

            this.$refs.modal.show()
            this.$nextTick(() => {
                if (this.$refs.products) {
                    this.$refs.products.click()
                }
            })
        },
        cleanSku(sku) {
            return sku.replace(/\s+/g, '');
        },
        selectInternalProduct(index, internalProduct) {
            this.externalProducts[index].internalProduct = internalProduct
        },
        async connectProducts() {
            this.connectionStarted = true;
            let externalProductsWithInternalProduct = this.externalProducts.filter((externalProduct) => externalProduct.internalProduct !== null);
            let externalProductsWithoutInternalProduct = this.externalProducts.filter((externalProduct) => externalProduct.internalProduct === null);
            let mainProjectLanguageAlias = null;
            let selectedPriceTemplate = null;
            let selectedSalePriceTemplate = null;

            let priceTemplateUuids = [
                this.integration.product_settings.price_template_uuid,
                this.integration.product_settings.sale_price_template_uuid
            ].filter(uuid => uuid !== null);

            if (priceTemplateUuids.length) {
                await httpClient.get(this.priceTemplatesUrl, {
                    uuids: priceTemplateUuids
                }).then((response) => {
                    response.data.data.forEach((priceTemplate) => {
                        if (priceTemplate.uuid === this.integration.product_settings.price_template_uuid) {
                            selectedPriceTemplate = priceTemplate
                            return
                        }

                        selectedSalePriceTemplate = priceTemplate
                    })
                })
            }

            await httpClient.get('/customization/languages/list').then((response) => {
                response.data.data.forEach((language) => {
                    if (language.main) {
                        mainProjectLanguageAlias = language.alias
                    }
                })
            })

            await Promise.all(chunk(externalProductsWithoutInternalProduct, 10).map((externalProductWithoutInternalProduct, chunkIndex) => {
                return httpClient.post('/batch', {
                    test_1: true,
                    data: externalProductWithoutInternalProduct.map((externalProduct) => {
                        let requestBody = {
                            name: externalProduct.names[mainProjectLanguageAlias] ?? Object.values(externalProduct.names)[0],
                        };

                        let priceTemplates = [];

                        if (selectedPriceTemplate?.formula === null) {
                            let priceTemplate = null;

                            if (externalProduct.price) {
                                priceTemplate = {
                                    uuid: selectedPriceTemplate.uuid,
                                    value: externalProduct.price
                                };
                            }
                            if (priceTemplate) {
                                priceTemplates.push(priceTemplate);
                            }
                        }

                        if (selectedSalePriceTemplate?.formula === null) {
                            let salePriceTemplate = null;
                            
                            if (externalProduct.sale_price) {
                                salePriceTemplate = {
                                    uuid: selectedSalePriceTemplate.uuid,
                                    value: externalProduct.sale_price
                                };
                            }
                            if (salePriceTemplate) {
                                priceTemplates.push(salePriceTemplate);
                            }
                        }

                        if (priceTemplates.length > 0) {
                            requestBody.price_templates = priceTemplates
                        }

                        if (externalProduct.group_key !== null) {
                            requestBody.group_key = externalProduct.group_key
                        }

                        if (externalProduct.sku !== null) {
                            requestBody.sku = externalProduct.sku
                        }

                        return {
                            method: "POST",
                            path: "/catalog/product-items",
                            body: requestBody
                        }
                    })
                }).then(async (response) => {
                    let externalProductsWithoutInternalProductStep = chunkIndex * 10;
                    let requestForCreateRelationships = [];

                    response.data.data.forEach((response, createdProductIndex) => {
                        let indexInExternalProducts = externalProductsWithoutInternalProductStep + createdProductIndex;

                        if (response.status === 201) {
                            externalProductsWithoutInternalProduct[indexInExternalProducts].internalProduct = response.body

                            let fileUrls = externalProductsWithoutInternalProduct[indexInExternalProducts].images

                            fileUrls.forEach((fileUrl) => {
                                requestForCreateRelationships.push({
                                    method: "POST",
                                    path: "/file/file-relationships",
                                    body: {
                                        model_type: "product_item",
                                        model_uuid: response.body.uuid,
                                        origin_url: fileUrl
                                    }
                                })
                            }) 
                        }
                    });
                    for (const batch of chunk(requestForCreateRelationships, 10)) {
                        await httpClient.post('/batch', {test_2: true, data: batch});
                    }
                })
            }))

            if (this.needCreateArrivalDeclaration) {
                let arrivalDeclarationStatusDone = await httpClient.get(`/management/arrival-declaration-statuses`, {
                    limit: 1,
                    types: ["done"]
                })
                let productsForArrival = externalProductsWithoutInternalProduct.filter(externalProduct => externalProduct.balance > 0);

                if (productsForArrival.length > 0) {
                    const createdArrivalDeclaration = await httpClient.post('/management/arrival-declarations', {
                        warehouse_id: this.warehouse.id,
                        supplier_id: this.supplier.id,
                        currency_uuid: this.currency.uuid,
                        currency_rate: this.currency.rate
                    }).then((response) => response.data.data);

                    await Promise.all(
                        chunk(productsForArrival, 10).map((externalProductsWithoutInternalProductChunk) =>
                            httpClient.post('/batch', {
                                test_5: true,
                                data: externalProductsWithoutInternalProductChunk.map((externalProductWithoutInternalProduct) => ({
                                    method: "POST",
                                    path: `/management/arrival-declarations/${createdArrivalDeclaration.uuid}/product-items`,
                                    body: {
                                        amount: externalProductWithoutInternalProduct.balance,
                                        cost: 0,
                                        product_item_id: externalProductWithoutInternalProduct.internalProduct.id
                                    }
                                }))
                            })
                        )
                    );

                    await httpClient.put(`/management/arrival-declarations/${createdArrivalDeclaration.uuid}/update-status`, {
                        status_id: arrivalDeclarationStatusDone.data.data[0].id
                    });
                }
            }

            let requestData = [];

            chunk(externalProductsWithInternalProduct, 10).forEach(chunkedExternalProducts => {
                const requestArrayData = [];

                requestArrayData.push(
                    httpClient.post("/batch", {
                        test_6: true,
                        data: chunkedExternalProducts.map((externalProduct) => ({
                            method: "PATCH",
                            path: `/integrations/products/${externalProduct.uuid}`,
                            body: {
                                product_item_uuid: externalProduct.internalProduct.uuid
                            }
                        }))
                    })
                )

                if (selectedPriceTemplate) {
                    requestArrayData.push(
                        httpClient.post("/batch", {
                            test_10: true,
                            data: chunkedExternalProducts.map((externalProduct) => ({
                                method: "PATCH",
                                path: `/catalog/product-items/${externalProduct.internalProduct.uuid}`,
                                body: {
                                    price_templates: [
                                        {
                                            uuid: selectedPriceTemplate.uuid,
                                            value: externalProduct.price
                                        }
                                    ]
                                }
                            }))
                        })
                    )
                }

                requestData.push(...requestArrayData)
            })

            externalProductsWithoutInternalProduct = externalProductsWithoutInternalProduct.filter((externalProduct) => externalProduct.internalProduct !== null)

            chunk(externalProductsWithoutInternalProduct, 10).forEach(chunkedExternalProducts => {
                const requestArrayData = [];

                requestArrayData.push(
                    httpClient.post("/batch", {
                        test_11: true,
                        data: chunkedExternalProducts.map((externalProduct) => ({
                            method: "PATCH",
                            path: `/integrations/products/${externalProduct.uuid}`,
                            body: {
                                product_item_uuid: externalProduct.internalProduct.uuid
                            }
                        }))
                    })
                )

                requestData.push(...requestArrayData)
            })

            this.externalProducts = [];

            await Promise.all(requestData).then((response) => {
                this.$emit('update:table')
                new MessageNotificator().success(translate('alerts.success'))
            })
            
            this.$refs.modal.hide()
            this.connectionStarted = false;
        },
        async fetchWarehouses() {
            await httpClient.get('/management/warehouses', {limit:0}).then((response) => {
                this.warehouses = response.data.data
            });
        },
        async fetchSuppliers() {
            await httpClient.get('/management/suppliers').then((response) => {
                this.suppliers = response.data.data
            });
        },
        async fetchCurrencies() {
            await httpClient.get('/list/currencies').then((response) => {
                this.currencies = response.data.data;
            });
        },
    },
    computed: {
        priceTemplatesUrl() {
            return `/projects/${this.$route.params.projectUuid}/pim/price-templates`;
        },
        warehousesOptions() {
            return this.warehouses.reduce((options, warehouse) => {
                if (warehouse.name.toLowerCase().includes(this.search.toLowerCase())) {
                    options.push(warehouse);
                }
                return options;
            }, []);
        },
        mainCurrency() {
            return this.currencies.find(currency => currency.main);
        }
    }
}
</script>
<style scoped>
.name-cell {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.table-image {
    max-width: 50px;
    max-height: 50px;
}
.modal-footer {
    padding-top: 20px;
}
</style>